import { routePaths } from '@/app/router/routePaths';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import type { Match, TeamId } from '@/core/schemas';
import { MatchRow } from '@/features/matches/MatchRow';
import { roundLabel } from '@/features/matches/matchPresentation';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { Alert } from '@/shared/ui/Alert';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

export function SchedulePage() {
  usePageTitle('Spielplan');
  const { matches, myTeam, lookup, rulesFor, phases, isLoading, errorMessage } =
    useTournamentData();

  if (isLoading) {
    return <LoadingScreen label="Spielplan wird geladen …" />;
  }

  if (errorMessage !== null) {
    return <PageState title="Spielplan nicht verfügbar" description={errorMessage} />;
  }

  if (matches.length === 0) {
    return (
      <Alert tone="info">
        Der Spielplan wird erstellt, sobald die Turnierleitung das Turnier startet.
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <h1 className="text-2xl font-bold tracking-tight">Spielplan</h1>

      {phases
        .filter((phase) => phase.matches.length > 0)
        .map((phase) => (
          <section key={phase.phaseId} className="flex flex-col gap-4">
            <h2 className="text-lg font-semibold">{phase.label}</h2>

            {groupByRound(phase.matches).map(([roundKey, roundMatches]) => (
              <div key={roundKey} className="flex flex-col gap-2">
                <h3 className="text-fg-subtle text-xs tracking-widest uppercase">
                  {roundLabel(roundKey)}
                </h3>
                <ul className="flex flex-col gap-2">
                  {roundMatches.map((match) => (
                    <MatchRow
                      key={match.id}
                      match={match}
                      lookup={lookup}
                      rules={rulesFor(match)}
                      highlightTeamId={myTeam?.id}
                      to={
                        ownMatch(match, myTeam?.id)
                          ? routePaths.tournament.match(match.id)
                          : undefined
                      }
                    />
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ))}
    </div>
  );
}

/** Only the captain's own matches are reportable, so only those link out. */
function ownMatch(match: Match, teamId: TeamId | undefined): boolean {
  if (teamId === undefined) {
    return false;
  }
  return (
    (match.home.kind === 'team' && match.home.teamId === teamId) ||
    (match.away.kind === 'team' && match.away.teamId === teamId)
  );
}

/** Keeps the schedule order while grouping consecutive matches by round. */
function groupByRound(matches: readonly Match[]): [string, Match[]][] {
  const rounds = new Map<string, Match[]>();

  for (const match of matches) {
    const key = String(match.roundKey);
    rounds.set(key, [...(rounds.get(key) ?? []), match]);
  }

  return [...rounds.entries()];
}
